/**
 * FERP Portal - DAGNode List Page
 *
 * Management page for DAG workflow graph nodes.
 * Part of the EXECUTE phase primitives.
 *
 * NOTE: Backend uses DagWorkflow query, not a list query.
 * This page provides a workflow-based view of DAG nodes.
 */

import { useMemo, useState } from 'react';
import { useTerminology } from '@ferp/workflow-forms';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  Badge,
  Button,
  Table,
  ThemeIcon,
  Loader,
  Alert,
  Card,
  Select,
} from '@mantine/core';
import {
  IconTopologyStar3,
  IconRefresh,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListWorkflowsQuery,
  useDagWorkflowByIdQuery,
  DagcriticalityEnum,
  DagnodeStatusEnum,
} from '@/generated/graphql';

export function DAGNodeListPage() {
  const { translate } = useTerminology();
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);

  // Fetch workflows to allow selection
  const { data: workflowsData, loading: workflowsLoading } = useListWorkflowsQuery({
    variables: { limit: 100 },
    skip,
  });

  // Fetch DAG for selected workflow
  const { data: dagData, loading: dagLoading, refetch: refetchDag } = useDagWorkflowByIdQuery({
    variables: { workflowId: selectedWorkflowId ?? '' },
    skip: skip || !selectedWorkflowId,
  });

  // Build workflow options
  const workflowOptions = useMemo(() => {
    const workflows = workflowsData?.listWorkflows?.items ?? [];
    return workflows.map((w) => ({
      value: w.id,
      label: `${w.name} (v${w.version})`,
    }));
  }, [workflowsData]);

  // Get nodes from selected DAG
  const nodes = useMemo(() => {
    return dagData?.dagWorkflow?.nodes ?? [];
  }, [dagData]);

  const edges = useMemo(() => {
    return dagData?.dagWorkflow?.edges ?? [];
  }, [dagData]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view DAG nodes.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>DAG Nodes</Title>
            <Text c="dimmed" size="sm">
              {translate('workflow')} execution graph vertices
            </Text>
          </div>
          {selectedWorkflowId && (
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={() => refetchDag()}
              loading={dagLoading}
            >
              Refresh
            </Button>
          )}
        </Group>

        {/* Workflow Selector */}
        <Card withBorder p="md">
          <Group gap="md">
            <ThemeIcon variant="light" size="lg" color="green">
              <IconTopologyStar3 size={20} />
            </ThemeIcon>
            <div style={{ flex: 1 }}>
              <Text size="sm" fw={500} mb={4}>Select Workflow</Text>
              <Select
                placeholder="Choose a workflow to view its DAG"
                data={workflowOptions}
                value={selectedWorkflowId}
                onChange={setSelectedWorkflowId}
                disabled={workflowsLoading}
                searchable
                clearable
              />
            </div>
          </Group>
        </Card>

        {/* Content */}
        {!selectedWorkflowId ? (
          <Card withBorder p="xl" ta="center">
            <Stack ta="center" gap="md">
              <ThemeIcon size="xl" variant="light" color="gray">
                <IconInfoCircle size={32} />
              </ThemeIcon>
              <Text c="dimmed">
                Select a workflow to view its DAG nodes
              </Text>
            </Stack>
          </Card>
        ) : dagLoading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Stack gap="md">
            {/* Stats */}
            <Group gap="md">
              <Card withBorder p="md" style={{ flex: 1 }}>
                <Text size="xs" c="dimmed">Total Nodes</Text>
                <Text size="xl" fw={700}>{nodes.length}</Text>
              </Card>
              <Card withBorder p="md" style={{ flex: 1 }}>
                <Text size="xs" c="dimmed">Total Edges</Text>
                <Text size="xl" fw={700}>{edges.length}</Text>
              </Card>
              {dagData?.dagWorkflow && (
                <Card withBorder p="md" style={{ flex: 1 }}>
                  <Text size="xs" c="dimmed">DAG Version</Text>
                  <Text size="xl" fw={700}>{dagData.dagWorkflow.version}</Text>
                </Card>
              )}
            </Group>

            {/* Nodes Table */}
            <Paper withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Node</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Criticality</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {nodes.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <Text ta="center" c="dimmed" py="md">
                          No nodes in this DAG
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    nodes.map((node) => (
                      <Table.Tr key={node.id}>
                        <Table.Td>
                          <Text fw={500}>{node.label}</Text>
                          <Text size="xs" c="dimmed">{node.id}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">
                            {node.nodeType}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            color={node.criticality === DagcriticalityEnum.lifeCritical ? 'red' : 'blue'}
                          >
                            {node.criticality}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            variant="outline"
                            color={
                              node.status === DagnodeStatusEnum.completed ? 'green' :
                              node.status === DagnodeStatusEnum.running ? 'blue' :
                              node.status === DagnodeStatusEnum.failed ? 'red' :
                              'gray'
                            }
                          >
                            {node.status}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Paper>

            {/* Edges Info */}
            {edges.length > 0 && (
              <Card withBorder p="md">
                <Text size="sm" fw={500} mb="xs">Graph Edges</Text>
                <Group gap="xs" wrap="wrap">
                  {edges.slice(0, 10).map((edge, i) => (
                    <Badge key={i} variant="outline" size="sm">
                      {edge.source} → {edge.target}
                    </Badge>
                  ))}
                  {edges.length > 10 && (
                    <Badge variant="light" size="sm" color="gray">
                      +{edges.length - 10} more
                    </Badge>
                  )}
                </Group>
              </Card>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default DAGNodeListPage;
